import {
	fetchTeacherDetailPageData,
	type TeacherDetailPageData,
} from "~/utils/page-data/teacher-detail";
import { useI18n } from "~/composables/useI18n";
import { isPageBffDebugEnabled, logPageBff } from "~/utils/page-data/debug-page-bff";

export function useTeacherDetailPageData(teacherId: string | number) {
	const { locale } = useI18n();
	const cacheKey = computed(() => `teacher-detail-${teacherId}-${locale.value}`);

	const { data, refresh, pending, error } = useLazyAsyncData<TeacherDetailPageData>(
		cacheKey,
		() => fetchTeacherDetailPageData(teacherId, locale.value),
		{
			default: () => ({
				teacherInfo: {},
				consultService: [],
				fahuiService: [],
				mediaContent: {
					horizVideo: [],
					vertVideo: [],
					articleList: [],
				},
			}),
			server: true,
			watch: [locale],
		}
	);

	const teacherInfo = computed(() => data.value?.teacherInfo || {});
	const consultService = computed(() => data.value?.consultService || []);
	const fahuiService = computed(() => data.value?.fahuiService || []);
	const mediaContent = computed(
		() =>
			data.value?.mediaContent || {
				horizVideo: [],
				vertVideo: [],
				articleList: [],
			}
	);

	// SSR 首屏数据来自 payload，客户端不一定会再打 BFF；调试时把关键字段打到浏览器控制台
	if (import.meta.client) {
		watch(
			teacherInfo,
			info => {
				if (!isPageBffDebugEnabled() || !info || !Object.keys(info).length) return;
				logPageBff("teacher-detail hydrated teacherInfo", {
					uid: info.uid,
					area_type: info.area_type,
					big_teacher_pc_image: info.big_teacher_pc_image,
					big_teacher_image: info.big_teacher_image,
					teacherInfo: info,
				});
			},
			{ immediate: true }
		);
	}

	return {
		data,
		teacherInfo,
		consultService,
		fahuiService,
		mediaContent,
		refresh,
		pending,
		error,
	};
}
